import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class LocalStorageService {
    private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    constructor() {}

    /**
     * Sets the value of the pair identified by key to value,
     * creating a new key/value pair if none existed for key previously.
     */
    setItem(key: string, value: string): void {
        if (!this.isBrowser) return;
        
        try {
            localStorage.setItem(key, value);
        } catch (error) {
            console.warn(`Failed to save item '${key}' to localStorage:`, error);
        }
    }

    /**
     * Returns the current value associated with the given key,
     * or null if the given key does not exist in the list associated
     * with the object.
     */
    getItem(key: string): string | null {
        if (!this.isBrowser) return null;
        
        try {
            return localStorage.getItem(key);
        } catch (error) {
            console.warn(`Failed to get item '${key}' from localStorage:`, error);
            return null;
        }
    }

    /**
     * Removes the key/value pair with the given key,
     * if a key/value pair with the given key exists.
     */
    removeItem(key: string): void {
        if (!this.isBrowser) return;
        
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.warn(`Failed to remove item '${key}' from localStorage:`, error);
        }
    }

    /**
     * Removes all key/value pairs, if there are any.
     */
    clear(): void {
        if (!this.isBrowser) return;
        
        try {
            localStorage.clear();
        } catch (error) {
            console.warn('Failed to clear localStorage:', error);
        }
    }

    /**
     * Generic method to get a typed value from localStorage with JSON parsing
     */
    getTypedItem<T>(key: string, defaultValue: T): T {
        if (!this.isBrowser) return defaultValue;
        
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : defaultValue;
        } catch (error) {
            console.warn(`Failed to parse item '${key}' from localStorage:`, error);
            return defaultValue;
        }
    }

    /**
     * Generic method to set a typed value to localStorage with JSON serialization
     */
    setTypedItem<T>(key: string, value: T): void {
        if (!this.isBrowser) return;
        
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.warn(`Failed to save typed item '${key}' to localStorage:`, error);
        }
    }
}
